import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { supabase } from '../../lib/supabase';

const STATUS_COLORS = {
     pending: 'bg-orange-500/20 text-orange-300',
     confirmed: 'bg-green-500/20 text-green-300',
     completed: 'bg-blue-500/20 text-blue-300',
     cancelled: 'bg-red-500/20 text-red-300',
};

const CHECKOUT_COLORS = {
     pending: 'bg-gray-50 text-darker/40',
     checked_out: 'bg-orange-500/20 text-orange-300',
     checked_in: 'bg-green-500/20 text-green-300',
};

export default function BookingDetail() {
     const { id } = useParams();
     const navigate = useNavigate();
     const [booking, setBooking] = useState(null);
     const [equipmentLogs, setEquipmentLogs] = useState([]);
     const [loading, setLoading] = useState(true);
     const [actionLoading, setActionLoading] = useState(false);
     const [notes, setNotes] = useState('');

     // Check-out/check-in form state
     const [checkoutNotes, setCheckoutNotes] = useState('');
     const [checkinNotes, setCheckinNotes] = useState('');
     const [damageDesc, setDamageDesc] = useState('');
     const [damageCost, setDamageCost] = useState('');
     const [showCheckinForm, setShowCheckinForm] = useState(false);

     useEffect(() => {
          fetchBooking();
          // eslint-disable-next-line react-hooks/exhaustive-deps
     }, [id]);

     async function fetchBooking() {
          const [bookingRes, logsRes] = await Promise.all([
               supabase.from('bookings').select('*').eq('id', id).single(),
               supabase
                    .from('equipment_logs')
                    .select('*')
                    .eq('booking_id', id)
                    .order('created_at', { ascending: false }),
          ]);
          setBooking(bookingRes.data);
          setNotes(bookingRes.data?.notes || '');
          setEquipmentLogs(logsRes.data || []);
          setLoading(false);
     }

     async function updateStatus(status) {
          setActionLoading(true);
          if (status === 'cancelled' && booking.stripe_payment_intent) {
               const { error } = await supabase.functions.invoke('cancel-booking', {
                    body: { bookingId: id },
               });
               if (error) {
                    alert('Error cancelling booking: ' + error.message);
                    setActionLoading(false);
                    return;
               }
          } else {
               await supabase.from('bookings').update({ status }).eq('id', id);
          }
          await fetchBooking();
          setActionLoading(false);
     }

     async function handleCheckout() {
          if (!window.confirm('Mark equipment as checked out?')) return;
          setActionLoading(true);

          await Promise.all([
               supabase.from('bookings').update({
                    checkout_status: 'checked_out',
                    checkout_notes: checkoutNotes,
               }).eq('id', id),
               supabase.from('equipment_logs').insert({
                    booking_id: id,
                    equipment_id: booking.equipment_id,
                    action: 'checked_out',
                    condition_notes: checkoutNotes,
               }),
          ]);

          setCheckoutNotes('');
          await fetchBooking();
          setActionLoading(false);
     }

     async function handleCheckin() {
          if (!window.confirm('Mark equipment as checked in / returned?')) return;
          setActionLoading(true);

          const costCents = damageCost ? Math.round(parseFloat(damageCost) * 100) : 0;

          await Promise.all([
               supabase.from('bookings').update({
                    checkout_status: 'checked_in',
                    checkin_notes: checkinNotes,
                    damage_description: damageDesc || null,
                    damage_cost: costCents,
                    status: 'completed',
               }).eq('id', id),
               supabase.from('equipment_logs').insert({
                    booking_id: id,
                    equipment_id: booking.equipment_id,
                    action: 'checked_in',
                    condition_notes: checkinNotes,
                    damage_description: damageDesc || null,
                    damage_cost: costCents,
               }),
          ]);

          setCheckinNotes('');
          setDamageDesc('');
          setDamageCost('');
          setShowCheckinForm(false);
          await fetchBooking();
          setActionLoading(false);
     }

     async function handleDepositRefund(action) {
          setActionLoading(true);
          const actionMap = { refund: 'full_refund', partial: 'partial_refund', keep: 'keep' };
          const { data, error } = await supabase.functions.invoke('refund-deposit', {
               body: { bookingId: booking.id, action: actionMap[action] },
          });
          if (error) {
               alert('Error processing deposit: ' + error.message);
          } else if (data?.refundAmount > 0) {
               alert(`Refund of $${(data.refundAmount / 100).toFixed(2)} issued via Stripe.`);
          }
          await fetchBooking();
          setActionLoading(false);
     }

     async function saveNotes() {
          await supabase.from('bookings').update({ notes }).eq('id', id);
          await fetchBooking();
     }

     if (loading) return <p className='text-darker/60'>Loading booking...</p>;
     if (!booking) return <p className='text-darker/60'>Booking not found.</p>;

     const inputClasses = 'w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-darker placeholder-darker/30 focus:outline-none focus:border-yellow duration-300';

     return (
          <div>
               <button
                    onClick={() => navigate('/admin/bookings')}
                    className='text-darker/40 text-sm hover:text-darker duration-300 mb-4'
               >
                    ← Back to Bookings
               </button>

               <div className='flex flex-wrap items-center gap-3 mb-6'>
                    <h1 className='text-darker text-2xl font-bold'>{booking.customer_name}</h1>
                    <span className={`px-2 py-1 rounded-md text-xs capitalize ${STATUS_COLORS[booking.status] || ''}`}>
                         {booking.status}
                    </span>
                    <span className={`px-2 py-1 rounded-md text-xs capitalize ${CHECKOUT_COLORS[booking.checkout_status || 'pending']}`}>
                         {booking.checkout_status === 'checked_out' ? 'Equipment Out' :
                          booking.checkout_status === 'checked_in' ? 'Returned' : 'Not Dispatched'}
                    </span>
                    {booking.customer_id && (
                         <Link to={`/admin/customers/${booking.customer_id}`} className='text-yellow text-sm hover:underline'>
                              View Customer →
                         </Link>
                    )}
               </div>

               {/* Order Summary - full width */}
               <div className='bg-white rounded-xl p-5 border border-gray-200 mb-6'>
                    <h3 className='text-yellow text-sm uppercase tracking-wider mb-4'>Order Summary</h3>
                    <div className='space-y-2 text-darker text-sm'>
                         {booking.equipment_description && (
                              <div>
                                   <div className='flex justify-between mb-1'>
                                        <span className='text-darker/40'>Equipment</span>
                                        <span className='text-darker/40 capitalize'>{booking.equipment_mode || 'package'}</span>
                                   </div>
                                   <ul className='space-y-1 ml-4 mb-2'>
                                        {booking.equipment_description.split(', ').map((item, i) => {
                                             const qtyMatch = item.match(/^(.+?)(\s+x(\d+))$/);
                                             return (
                                                  <li key={i} className='text-darker/80 flex items-center gap-2'>
                                                       <span className='text-yellow text-xs'>-</span>
                                                       {qtyMatch ? (
                                                            <>
                                                                 {qtyMatch[1]}
                                                                 <span className='text-yellow font-bold ml-1'>x{qtyMatch[3]}</span>
                                                            </>
                                                       ) : item}
                                                  </li>
                                             );
                                        })}
                                   </ul>
                              </div>
                         )}
                         {(booking.rental_cents || 0) > 0 && (
                              <div className='flex justify-between'>
                                   <span className='text-darker/40'>Rental</span>
                                   <span>${(booking.rental_cents / 100).toLocaleString()}</span>
                              </div>
                         )}
                         {booking.delivery_method && booking.delivery_method !== 'pickup' && (
                              <div className='flex justify-between'>
                                   <span className='text-darker/40'>Delivery</span>
                                   <span>${((booking.delivery_fee_cents || 0) / 100).toLocaleString()}</span>
                              </div>
                         )}
                         {booking.delivery_method === 'pickup' && (
                              <div className='flex justify-between'>
                                   <span className='text-darker/40'>Delivery</span>
                                   <span className='text-darker/60'>Self Pickup</span>
                              </div>
                         )}
                         {(booking.deposit_cents || 0) > 0 && (
                              <div className='flex justify-between'>
                                   <span className='text-darker/40'>Security Deposit</span>
                                   <span>${(booking.deposit_cents / 100).toLocaleString()}</span>
                              </div>
                         )}
                         {(booking.convenience_fee_cents || 0) > 0 && (
                              <div className='flex justify-between'>
                                   <span className='text-darker/40'>Convenience Fee (3.2%)</span>
                                   <span>${(booking.convenience_fee_cents / 100).toFixed(2)}</span>
                              </div>
                         )}
                         <div className='flex justify-between border-t border-gray-200 pt-2 mt-2 text-base'>
                              <span className='font-bold'>Total</span>
                              <span className='text-yellow font-bold'>${(booking.total_cents / 100).toLocaleString()}</span>
                         </div>
                    </div>
               </div>

               <div className='grid grid-cols-1 tablet:grid-cols-2 gap-6'>
                    {/* Booking Details */}
                    <div className='bg-white rounded-xl p-5 border border-gray-200'>
                         <h3 className='text-yellow text-sm uppercase tracking-wider mb-4'>Booking Details</h3>
                         <div className='space-y-3 text-darker'>
                              <div className='flex justify-between'>
                                   <span className='text-darker/40'>Start Date</span>
                                   <span>{format(new Date(booking.start_date), 'MMM d, yyyy')}</span>
                              </div>
                              <div className='flex justify-between'>
                                   <span className='text-darker/40'>End Date</span>
                                   <span>{format(new Date(booking.end_date), 'MMM d, yyyy')}</span>
                              </div>
                              <div className='flex justify-between'>
                                   <span className='text-darker/40'>Total</span>
                                   <span className='text-yellow font-bold'>${(booking.total_cents / 100).toLocaleString()}</span>
                              </div>
                              <div className='flex justify-between'>
                                   <span className='text-darker/40'>Created</span>
                                   <span className='text-sm'>{format(new Date(booking.created_at), 'MMM d, yyyy h:mm a')}</span>
                              </div>
                              {(booking.convenience_fee_cents || 0) > 0 && (
                                   <div className='flex justify-between'>
                                        <span className='text-darker/40'>Convenience Fee</span>
                                        <span className='text-sm'>${(booking.convenience_fee_cents / 100).toFixed(2)}</span>
                                   </div>
                              )}
                              {booking.stripe_payment_intent && (
                                   <div className='flex justify-between'>
                                        <span className='text-darker/40'>Stripe ID</span>
                                        <span className='text-sm text-darker/60 font-mono'>{booking.stripe_payment_intent}</span>
                                   </div>
                              )}
                              {booking.delivery_address && (
                                   <div className='flex justify-between'>
                                        <span className='text-darker/40'>Delivery</span>
                                        <span className='text-sm text-right'>{booking.delivery_address}</span>
                                   </div>
                              )}
                         </div>
                    </div>

                    {/* Contact Info */}
                    <div className='bg-white rounded-xl p-5 border border-gray-200'>
                         <h3 className='text-yellow text-sm uppercase tracking-wider mb-4'>Contact Information</h3>
                         <div className='space-y-3 text-darker'>
                              <div className='flex justify-between'>
                                   <span className='text-darker/40'>Name</span>
                                   <span>{booking.customer_name}</span>
                              </div>
                              <div className='flex justify-between'>
                                   <span className='text-darker/40'>Email</span>
                                   <a href={`mailto:${booking.customer_email}`} className='text-yellow hover:underline'>{booking.customer_email}</a>
                              </div>
                              <div className='flex justify-between'>
                                   <span className='text-darker/40'>Phone</span>
                                   <a href={`tel:${booking.customer_phone}`} className='text-yellow hover:underline'>{booking.customer_phone}</a>
                              </div>
                              {booking.delivery_address && (
                                   <div className='border-t border-gray-200 pt-3 mt-3'>
                                        <span className='text-darker/40 text-xs block mb-1'>Delivery Address</span>
                                        <span className='text-darker'>{booking.delivery_address}</span>
                                   </div>
                              )}
                         </div>
                    </div>

                    {/* Equipment Check-out / Check-in */}
                    <div className='bg-white rounded-xl p-5 border border-gray-200'>
                         <h3 className='text-yellow text-sm uppercase tracking-wider mb-4'>Equipment Workflow</h3>

                         {/* Check-out */}
                         {booking.status === 'confirmed' && booking.checkout_status !== 'checked_out' && booking.checkout_status !== 'checked_in' && (
                              <div className='space-y-3'>
                                   <p className='text-darker/60 text-sm'>Record the equipment condition before dispatch.</p>
                                   <textarea
                                        value={checkoutNotes}
                                        onChange={(e) => setCheckoutNotes(e.target.value)}
                                        placeholder='Checkout condition notes (e.g., all items accounted for, equipment clean)...'
                                        rows={3}
                                        className={inputClasses}
                                   />
                                   <button
                                        onClick={handleCheckout}
                                        disabled={actionLoading}
                                        className='w-full px-4 py-2 rounded-lg text-sm text-orange-300 hover:bg-orange-500/10 border border-orange-500/30 duration-300 disabled:opacity-50'
                                   >
                                        Check Out Equipment
                                   </button>
                              </div>
                         )}

                         {/* Checked out — show check-in option */}
                         {booking.checkout_status === 'checked_out' && (
                              <div className='space-y-3'>
                                   {booking.checkout_notes && (
                                        <div className='bg-gray-50 rounded-lg p-3 text-sm'>
                                             <p className='text-darker/40 text-xs mb-1'>Checkout Notes:</p>
                                             <p className='text-darker/80'>{booking.checkout_notes}</p>
                                        </div>
                                   )}

                                   {!showCheckinForm ? (
                                        <button
                                             onClick={() => setShowCheckinForm(true)}
                                             className='w-full px-4 py-2 rounded-lg text-sm text-green-300 hover:bg-green-500/10 border border-green-500/30 duration-300'
                                        >
                                             Check In / Return Equipment
                                        </button>
                                   ) : (
                                        <div className='space-y-3 border-t border-gray-200 pt-3'>
                                             <p className='text-darker/60 text-sm'>Record equipment return condition.</p>
                                             <div>
                                                  <label className='block text-darker/40 text-xs mb-1'>Return Condition Notes</label>
                                                  <textarea
                                                       value={checkinNotes}
                                                       onChange={(e) => setCheckinNotes(e.target.value)}
                                                       placeholder='Equipment condition on return...'
                                                       rows={2}
                                                       className={inputClasses}
                                                  />
                                             </div>
                                             <div>
                                                  <label className='block text-darker/40 text-xs mb-1'>Damage Description (if any)</label>
                                                  <textarea
                                                       value={damageDesc}
                                                       onChange={(e) => setDamageDesc(e.target.value)}
                                                       placeholder='Describe any damage found...'
                                                       rows={2}
                                                       className={inputClasses}
                                                  />
                                             </div>
                                             {damageDesc && (
                                                  <div>
                                                       <label className='block text-darker/40 text-xs mb-1'>Estimated Repair Cost ($)</label>
                                                       <input
                                                            type='number'
                                                            min='0'
                                                            step='0.01'
                                                            value={damageCost}
                                                            onChange={(e) => setDamageCost(e.target.value)}
                                                            placeholder='0.00'
                                                            className={inputClasses + ' max-w-[200px]'}
                                                       />
                                                  </div>
                                             )}
                                             <div className='flex gap-2'>
                                                  <button
                                                       onClick={handleCheckin}
                                                       disabled={actionLoading}
                                                       className='px-4 py-2 rounded-lg text-sm text-green-300 hover:bg-green-500/10 border border-green-500/30 duration-300 disabled:opacity-50'
                                                  >
                                                       Confirm Check-In
                                                  </button>
                                                  <button
                                                       onClick={() => setShowCheckinForm(false)}
                                                       className='px-4 py-2 rounded-lg text-sm text-darker/40 hover:text-darker border border-gray-200 duration-300'
                                                  >
                                                       Cancel
                                                  </button>
                                             </div>
                                        </div>
                                   )}
                              </div>
                         )}

                         {/* Checked in — show summary */}
                         {booking.checkout_status === 'checked_in' && (
                              <div className='space-y-3'>
                                   <div className='flex items-center gap-2 text-green-300 text-sm'>
                                        <span className='w-2 h-2 rounded-full bg-green-400'></span>
                                        Equipment returned
                                   </div>
                                   {booking.checkout_notes && (
                                        <div className='bg-gray-50 rounded-lg p-3 text-sm'>
                                             <p className='text-darker/40 text-xs mb-1'>Checkout Notes:</p>
                                             <p className='text-darker/80'>{booking.checkout_notes}</p>
                                        </div>
                                   )}
                                   {booking.checkin_notes && (
                                        <div className='bg-gray-50 rounded-lg p-3 text-sm'>
                                             <p className='text-darker/40 text-xs mb-1'>Check-in Notes:</p>
                                             <p className='text-darker/80'>{booking.checkin_notes}</p>
                                        </div>
                                   )}
                                   {booking.damage_description && (
                                        <div className='bg-red-50 border border-red-200 rounded-lg p-3 text-sm'>
                                             <p className='text-red-700 text-xs mb-1'>Damage Report:</p>
                                             <p className='text-darker/80'>{booking.damage_description}</p>
                                             {(booking.damage_cost || 0) > 0 && (
                                                  <p className='text-red-700 mt-1'>
                                                       Repair cost: ${(booking.damage_cost / 100).toFixed(2)}
                                                  </p>
                                             )}
                                        </div>
                                   )}
                              </div>
                         )}
                    </div>

                    {/* Security Deposit */}
                    {(booking.deposit_cents || 0) > 0 && (
                         <div className='bg-white rounded-xl p-5 border border-gray-200'>
                              <h3 className='text-yellow text-sm uppercase tracking-wider mb-4'>Security Deposit</h3>
                              <div className='space-y-3 text-darker'>
                                   <div className='flex justify-between'>
                                        <span className='text-darker/40'>Deposit Amount</span>
                                        <span className='font-bold'>${(booking.deposit_cents / 100).toLocaleString()}</span>
                                   </div>
                                   <div className='flex justify-between items-center'>
                                        <span className='text-darker/40'>Status</span>
                                        <span className={`px-2 py-1 rounded-md text-xs capitalize ${
                                             booking.deposit_status === 'held' ? 'bg-orange-500/20 text-orange-300' :
                                             booking.deposit_status === 'refunded' ? 'bg-green-500/20 text-green-300' :
                                             booking.deposit_status === 'captured' ? 'bg-red-500/20 text-red-300' :
                                             'bg-gray-50 text-darker/40'
                                        }`}>
                                             {booking.deposit_status || 'none'}
                                        </span>
                                   </div>
                                   {(booking.damage_cost || 0) > 0 && (
                                        <div className='flex justify-between'>
                                             <span className='text-darker/40'>Damage Deduction</span>
                                             <span className='text-red-700'>${(booking.damage_cost / 100).toFixed(2)}</span>
                                        </div>
                                   )}
                              </div>

                              {booking.deposit_status === 'held' && booking.checkout_status === 'checked_in' && (
                                   <div className='mt-4 pt-3 border-t border-gray-200 space-y-2'>
                                        <button
                                             onClick={() => handleDepositRefund('refund')}
                                             disabled={actionLoading}
                                             className='w-full text-left px-4 py-2 rounded-lg text-sm text-green-300 hover:bg-green-500/10 border border-green-500/30 duration-300 disabled:opacity-50'
                                        >
                                             Full Refund — No damage
                                        </button>
                                        {(booking.damage_cost || 0) > 0 && (
                                             <button
                                                  onClick={() => handleDepositRefund('partial')}
                                                  disabled={actionLoading}
                                                  className='w-full text-left px-4 py-2 rounded-lg text-sm text-yellow hover:bg-yellow/10 border border-yellow/30 duration-300 disabled:opacity-50'
                                             >
                                                  Partial Refund — Deduct ${(booking.damage_cost / 100).toFixed(2)} for repairs
                                                  (refund ${(Math.max(0, (booking.deposit_cents || 0) - (booking.damage_cost || 0)) / 100).toFixed(2)})
                                             </button>
                                        )}
                                        <button
                                             onClick={() => {
                                                  if (window.confirm('Keep the entire deposit?')) handleDepositRefund('keep');
                                             }}
                                             disabled={actionLoading}
                                             className='w-full text-left px-4 py-2 rounded-lg text-sm text-red-700 hover:bg-red-500/10 border border-red-500/30 duration-300 disabled:opacity-50'
                                        >
                                             Keep Entire Deposit — Major damage / not returned
                                        </button>
                                   </div>
                              )}
                         </div>
                    )}

                    {/* Notes */}
                    <div className='bg-white rounded-xl p-5 border border-gray-200'>
                         <h3 className='text-yellow text-sm uppercase tracking-wider mb-4'>Notes</h3>
                         <textarea
                              value={notes}
                              onChange={(e) => setNotes(e.target.value)}
                              className={inputClasses}
                              rows={4}
                              placeholder='Add notes about this booking...'
                         />
                         <button onClick={saveNotes} className='mt-2 text-sm text-yellow hover:underline'>
                              Save Notes
                         </button>
                    </div>

                    {/* Actions */}
                    <div className='bg-white rounded-xl p-5 border border-gray-200'>
                         <h3 className='text-yellow text-sm uppercase tracking-wider mb-4'>Booking Actions</h3>
                         <div className='space-y-2'>
                              {booking.status === 'confirmed' && booking.checkout_status === 'checked_in' && (
                                   <button
                                        onClick={() => updateStatus('completed')}
                                        disabled={actionLoading}
                                        className='w-full text-left px-4 py-2 rounded-lg text-sm text-green-300 hover:bg-green-500/10 border border-green-500/30 duration-300 disabled:opacity-50'
                                   >
                                        Mark as Completed
                                   </button>
                              )}
                              {(booking.status === 'confirmed' || booking.status === 'pending') && (
                                   <button
                                        onClick={() => {
                                             if (window.confirm('Cancel this booking and issue a refund?')) updateStatus('cancelled');
                                        }}
                                        disabled={actionLoading}
                                        className='w-full text-left px-4 py-2 rounded-lg text-sm text-red-700 hover:bg-red-500/10 border border-red-500/30 duration-300 disabled:opacity-50'
                                   >
                                        Cancel & Refund
                                   </button>
                              )}
                              {(booking.status === 'completed' || booking.status === 'cancelled') && (
                                   <p className='text-darker/30 text-sm'>No actions available for {booking.status} bookings.</p>
                              )}
                         </div>
                    </div>
               </div>

               {/* Equipment Log History */}
               {equipmentLogs.length > 0 && (
                    <div className='mt-6 bg-white rounded-xl border border-gray-200 overflow-hidden'>
                         <div className='p-4 border-b border-gray-200'>
                              <h3 className='text-darker text-sm font-bold'>Equipment Log</h3>
                         </div>
                         <div className='divide-y divide-gray-200'>
                              {equipmentLogs.map((log) => (
                                   <div key={log.id} className='p-4'>
                                        <div className='flex items-center gap-3 mb-1'>
                                             <span className={`px-2 py-0.5 rounded text-xs ${
                                                  log.action === 'checked_out'
                                                       ? 'bg-orange-500/20 text-orange-300'
                                                       : 'bg-green-500/20 text-green-300'
                                             }`}>
                                                  {log.action === 'checked_out' ? 'Checked Out' : 'Checked In'}
                                             </span>
                                             <span className='text-darker/30 text-xs'>
                                                  {format(new Date(log.created_at), 'MMM d, yyyy h:mm a')}
                                             </span>
                                        </div>
                                        {log.condition_notes && (
                                             <p className='text-darker/60 text-sm'>{log.condition_notes}</p>
                                        )}
                                        {log.damage_description && (
                                             <p className='text-red-700 text-sm mt-1'>
                                                  Damage: {log.damage_description}
                                                  {(log.damage_cost || 0) > 0 && ` ($${(log.damage_cost / 100).toFixed(2)})`}
                                             </p>
                                        )}
                                   </div>
                              ))}
                         </div>
                    </div>
               )}
          </div>
     );
}
